import { FlexShrink } from '@Components/FlexShrink';
import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { costCenterColumns, ledgerAccountColumns, serviceColumns, vatColumns } from '@Constants/TableColumns';
import { buildPositionElementId } from '@Hooks/Navigation';
import {
    type ApiCostCenter,
    type ApiInvoicePosition,
    type ApiLedgerAccount,
    type ApiService,
    type ApiVat,
    EnumFrontendLayoutSetting,
    EnumInvoiceType,
    type EnumProcessState,
    EnumTaxationMode,
    getEnumUnitLabel,
    getPriceLabel,
    isApproved,
} from '@Model';
import { FrameEditCostCenter, FrameEditLedgerAccount, FrameEditService, FrameEditVat } from '@Modules/Stammdaten';
import {
    Badge,
    defaultIconSize,
    DivWithSpacing,
    FlexRow,
    getSpacings,
    Grid,
    GridColumn,
    GridColumns,
    type Index,
    isLinked,
    type ISubFrameBodyProps,
    LabeledDiv,
    NumberInput,
    TextInput,
    UnlabeledField,
    useGuiBinder,
    useLinkedFieldInFrame,
    useLinkedObjectOverlay,
    useLinkProperty,
    useProp,
    useTranslate,
    DateInput,
} from '@synqo/frontend-component-library';
import { IconCurrencyEuro } from '@tabler/icons-react';
import { useCallback } from 'react';
import { FrameEditPriceList } from './FrameEditPriceList';

interface InvoiceRefProps {
    priceDisplay: EnumTaxationMode;
    processState: EnumProcessState;
    invoiceType: EnumInvoiceType;
}

function forceReadOnly(props: InvoiceRefProps, name: string) {
    if (isApproved(props.processState) || props.invoiceType == EnumInvoiceType.Cancellation) {
        const allowedProperties: Index<ApiInvoicePosition>[] = ['cost_center', 'ledger_account'];
        return !allowedProperties.includes(name as Index<ApiInvoicePosition>);
    } else {
        return false;
    }
}

export function FrameInvoicePosition(props: ISubFrameBodyProps<ApiInvoicePosition> & InvoiceRefProps) {
    const i18n = useTranslate('');

    const pos: ApiInvoicePosition = props.data;

    const binder = useGuiBinder<ApiInvoicePosition>({
        ...props,
        forceReadOnly: (name) => forceReadOnly(props, name),
    });

    const linkService = useLinkedFieldInFrame(props, 'service', FrameEditService);
    const linkVat = useLinkedFieldInFrame(props, 'vat', FrameEditVat);
    const linkLedgerAccount = useLinkedFieldInFrame(props, 'ledger_account', FrameEditLedgerAccount);
    const linkCostCenter = useLinkedFieldInFrame(props, 'cost_center', FrameEditCostCenter);

    const serviceField = useLinkProperty(binder, linkService.linkApi);
    const dateOfPerformanceField = useProp(binder, 'date_of_performance');
    const labelField = useProp(binder, 'label');
    const quantityField = useProp<ApiInvoicePosition, number>(binder, 'quantity');
    const pricePerUnitField = useProp<ApiInvoicePosition, number>(binder, 'price_per_unit');
    const vatField = useLinkProperty(binder, linkVat.linkApi);
    const totalGrossPriceField = useProp<ApiInvoicePosition, number>(binder, 'total_gross_price');
    const totalNetPriceField = useProp<ApiInvoicePosition, number>(binder, 'total_net_price');
    const ledgerAccountField = useLinkProperty(binder, linkLedgerAccount.linkApi);
    const costCenterField = useLinkProperty(binder, linkCostCenter.linkApi);

    const { onClick: clickLinkedPriceList } = useLinkedObjectOverlay({
        Renderer: FrameEditPriceList,
        canAdd: false,
    });

    const nonNullLinkedPriceListId = pos.price_list.foreign_key ?? '';
    const openPriceListInModal = useCallback(() => {
        clickLinkedPriceList(nonNullLinkedPriceListId);
    }, [clickLinkedPriceList, nonNullLinkedPriceListId]);

    return (
        <DivWithSpacing id={buildPositionElementId(props.data.id)} padding={getSpacings('md')} className={'w-4/5'}>
            <Grid>
                <GridColumns spans={[6, 6]}>
                    <LinkedFieldWithLayoutSettings<ApiService>
                        layoutSettingsContext={EnumFrontendLayoutSetting.ServicesTableLayout}
                        {...serviceField}
                        {...linkService.events}
                        columns={serviceColumns}
                        label="Leistung"
                    />
                    <DateInput type="day" {...dateOfPerformanceField} label="Leistungsdatum" />
                </GridColumns>
                <GridColumn span={12}>
                    <TextInput {...labelField} label={i18n('Positionstext')} inputSize={2} />
                </GridColumn>
                <GridColumns spans={[3, 3, 6]}>
                    <NumberInput {...quantityField} label="Menge" decimals={pos.service.decimal_places_amount} />
                    <div className="flex-shrink">
                        <LabeledDiv htmlFor="unit" label="Einheit">
                            <Badge key="unit" variant="primary" type="input" size="sm">
                                {getEnumUnitLabel(pos.service.unit)}
                            </Badge>
                        </LabeledDiv>
                    </div>
                    <FlexRow grow>
                        <NumberInput
                            {...pricePerUnitField}
                            decimals={pos.service.decimal_places_price}
                            label="Einzelpreis in €"
                        />
                        {pos.price_per_unit !== 0 && (
                            <FlexShrink title={getPriceLabel(pos)}>
                                {isLinked(pos.price_list) ? (
                                    <UnlabeledField>
                                        <Badge
                                            key="price_list"
                                            type="input"
                                            variant="info"
                                            onClick={openPriceListInModal}
                                        >
                                            <IconCurrencyEuro size={defaultIconSize} />
                                            &nbsp;{getPriceLabel(pos)}
                                        </Badge>
                                    </UnlabeledField>
                                ) : (
                                    <UnlabeledField>
                                        <Badge key="price_list" type="input" variant="info">
                                            {getPriceLabel(pos)}
                                        </Badge>
                                    </UnlabeledField>
                                )}
                            </FlexShrink>
                        )}
                    </FlexRow>
                </GridColumns>
                <GridColumns spans={[6, 6]}>
                    <LinkedFieldWithLayoutSettings<ApiVat>
                        layoutSettingsContext={EnumFrontendLayoutSetting.VatsTableLayout}
                        {...vatField}
                        {...linkVat.events}
                        columns={vatColumns}
                        label="Mehrwertst."
                    />
                    {props.priceDisplay === EnumTaxationMode.PriceIsGross ? (
                        <NumberInput
                            {...totalGrossPriceField}
                            decimals={pos.service.decimal_places_price}
                            label="Gesamtpreis in € (Brutto)"
                            locked
                        />
                    ) : (
                        <NumberInput
                            {...totalNetPriceField}
                            decimals={pos.service.decimal_places_price}
                            label="Gesamtpreis in € (Netto)"
                            locked
                        />
                    )}
                </GridColumns>
                <GridColumns spans={[6, 6]}>
                    <LinkedFieldWithLayoutSettings<ApiLedgerAccount>
                        layoutSettingsContext={EnumFrontendLayoutSetting.LedgerAccountsTableLayout}
                        {...ledgerAccountField}
                        {...linkLedgerAccount.events}
                        columns={ledgerAccountColumns}
                        label="Sachkonto"
                    />
                    <LinkedFieldWithLayoutSettings<ApiCostCenter>
                        layoutSettingsContext={EnumFrontendLayoutSetting.CostCentersTableLayout}
                        {...costCenterField}
                        {...linkCostCenter.events}
                        columns={costCenterColumns}
                        label="Kostenstelle"
                    />
                </GridColumns>
            </Grid>
        </DivWithSpacing>
    );
}
